import { css } from "@emotion/css";
import type { PageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { ActionButton, ActionButtonType, Avatar, Breadcrumbs, Button, Callout, Dialog, SimpleDataTable, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, GitPersistenceSettings, GitRef, GitRefResource, IconMetadataResource, IconSvgResource, IProcessResource, ModifyProcessTemplateCommand, PlatformHubConnectionConfigurationResource, ProcessTemplateResource, ProcessTemplateVersionResource, Repository, ShareTemplateToManySpacesBffCommand, SpaceResource, ValidateGitRefV2Response, WorkerPoolsSummaryResource, } from "@octopusdeploy/octopus-server-client";
import { GetPrimaryPackageReference, isProcessTemplateParameterResource, OctopusError, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { noOp } from "@octopusdeploy/utilities";
import jsSHA from "jssha";
import { isEqual, keyBy } from "lodash";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { EditProcessTemplateSettingsTab } from "~/areas/blueprints/EditProcessTemplateSettingsTab";
import { lastAccessedPlatformHubGitRef } from "~/areas/blueprints/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/blueprints/PlatformHubPageHeaderBranchSelector";
import { buildProcessTemplateParametersActions, ProcessTemplateParametersTab } from "~/areas/blueprints/ProcessTemplateParametersTab";
import { ProcessTemplateWizardDialog, ProcessTemplateWizardSteps } from "~/areas/blueprints/ProcessTemplateWizardDialog";
import { getProcessTemplateIconUrl } from "~/areas/blueprints/ProcessTemplatesPage";
import type { SharedSpaces } from "~/areas/blueprints/ShareTemplateDialog";
import { StopShareTemplateDialog } from "~/areas/blueprints/StopSharingTemplateDialog";
import { useRequiredPlatformHubGitRef } from "~/areas/blueprints/usePlatformHubGitRef";
import type { ProcessBlueprintContextProps } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { buildProcessBlueprintContextProps, ProcessBlueprintContext } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { isRunOnServerOrWorkerPool, loadAvailableWorkerPools, whereConfiguredToRun } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions, useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { hasSteps } from "~/areas/projects/components/Process/Contexts/processModelSelectors";
import ProcessListLayout from "~/areas/projects/components/Process/ProcessListLayout";
import ProcessStepsLayout, { intentFromFilter, ProcessPageIntent } from "~/areas/projects/components/Process/ProcessStepsLayout";
import type { ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import { emptyVariableSetResource } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import type { ProcessTemplateProcessIdentifier } from "~/areas/projects/components/Process/types";
import { ExecutionLocation, isProcessTemplateProcessIdentifier, toProcessTemplateProcessIdentifier } from "~/areas/projects/components/Process/types";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { PersistenceSettingsContextProvider } from "~/areas/projects/context/PersistenceSettingsContext";
import { repository } from "~/clientInstance";
import type { ActionPlugin } from "~/components/Actions/pluginRegistry";
import Chip from "~/components/Chips/Chip";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import * as tenantTagsets from "~/components/tenantTagsets";
import useLocalStorage from "~/hooks/useLocalStorage";
import type { GlobalState } from "~/store/reducers";
import DateFormatter from "~/utils/DateFormatter/index";
import StringHelper from "~/utils/StringHelper";
interface EditProcessTemplateProps {
    loaderData: LoaderData;
    slug: string;
    queryParams: EditProcessTemplateQueryParams;
    setQueryParams: QueryParamValuesSetter<EditProcessTemplateQueryParams>;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
type EditProcessTemplateActiveTab = "process" | "parameters" | "settings" | "versions";
export interface EditProcessTemplateQueryParams {
    gitRef: string;
    activeTab: EditProcessTemplateActiveTab;
}
interface EditWithAllContextProps extends EditProcessTemplateProps {
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
}
interface EditProcessTemplateInternalProps {
    cleanModel: ProcessTemplateResource;
    model: ProcessTemplateResource;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    lookupData: EditProcessTemplateLookupData;
    icons: IconSvgResource[];
    iconMetadata: IconMetadataResource | undefined;
    onChange: (blueprint: ProcessTemplateResource) => void;
    gitRefResource: GitRefResource | undefined;
    gitRef: GitRef;
    setGitRef: (gitRef: string) => void;
    gitPersistenceSettings: GitPersistenceSettings;
    activeTab: EditProcessTemplateActiveTab;
    setActiveTab: (value: EditProcessTemplateActiveTab) => void;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
export interface EditProcessTemplateLookupData extends ProcessStepsLayoutLoaderLookupData {
    spaces: SpaceResource[];
}
function EditWithAllContext({ loaderData, slug, doBusyTask, busy, errors, queryParams, setQueryParams, scrollAreaRef }: EditWithAllContextProps) {
    const [gitRef, setGitRef, validatedGitRef] = useRequiredPlatformHubGitRef(loaderData.platformHubConnectionConfiguration.PersistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [lookupData, setLookupData] = useState<EditProcessTemplateLookupData>({ ...defaultLookups, templates: loaderData.processTemplate.Parameters });
    const [icons, setIcons] = useState<IconSvgResource[]>([]);
    const [iconMetadata, setIconMetadata] = useState<IconMetadataResource>();
    const { result: cleanModel, refetch: reloadProcessTemplate } = useQuery(async (repository) => {
        const result = await repository.ProcessTemplates.get(slug, gitRef);
        setModel(result);
        return result;
    }, [slug, gitRef], "Get process template", {
        initialResult: loaderData.processTemplate,
    });
    const [model, setModel] = useState<ProcessTemplateResource>(cleanModel);
    const [blueprintContextProps, setBlueprintContextProps] = useState<ProcessBlueprintContextProps>(buildProcessBlueprintContextProps(cleanModel));
    const refreshLookups = useCallback(async () => {
        const environments = repository.Environments.all();
        const tagSets = tenantTagsets.getAll();
        setLookupData({
            ...defaultLookups,
            environmentsById: keyBy(await environments, "Id"),
            tagSets: await tagSets,
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tenantTagsets.getTagIndex(),
            spaces: await repository.Spaces.all(),
        });
    }, []);
    const onChange = useCallback((blueprint: ProcessTemplateResource) => {
        const context = buildProcessBlueprintContextProps(blueprint);
        setBlueprintContextProps(context);
        setLookupData({ ...lookupData, templates: blueprint.Parameters });
        setModel(blueprint);
    }, [lookupData]);
    useDoBusyTaskEffect(doBusyTask, async () => {
        await refreshLookups();
        const icons = await repository.Icons.getIcons();
        setIcons(icons);
        const iconMetadata = await repository.Icons.getIconMetadata();
        setIconMetadata(iconMetadata);
    }, [refreshLookups]);
    const processIdentifier: ProcessTemplateProcessIdentifier = useMemo(() => toProcessTemplateProcessIdentifier(slug, gitRef), [slug, gitRef]);
    const setActiveTab = useCallback((value) => setQueryParams((prev) => ({ ...prev, activeTab: value })), [setQueryParams]);
    if (!validatedGitRef) {
        return <PageContent header={{ title: StringHelper.ellipsis }}>{null}</PageContent>;
    }
    return (<ProcessBlueprintContext.Provider value={blueprintContextProps}>
            <PersistenceSettingsContextProvider {...loaderData.platformHubConnectionConfiguration.PersistenceSettings}>
                <ProcessController layoutActions={{ refreshLookupData: refreshLookups }} doBusyTask={doBusyTask} processIdentifier={processIdentifier} process={cleanModel} reloadProcess={reloadProcessTemplate} // Only used when updating step templates which blueprints can't do yet, so this is actually unused
     modifyProcess={() => Promise.resolve()} // This function is never used for blueprints since the save logic is handled outside the process context
    >
                    {() => {
            return (<ProcessQueryStringController initialQueryFilter={createDefaultFilter()}>
                                {() => {
                    return (<EditProcessTemplatePageInternal cleanModel={cleanModel} model={model} doBusyTask={doBusyTask} busy={busy} errors={errors} lookupData={lookupData} gitRefResource={validatedGitRef?.GitRef} gitRef={gitRef} setGitRef={setGitRef} gitPersistenceSettings={loaderData.platformHubConnectionConfiguration.PersistenceSettings} icons={icons} iconMetadata={iconMetadata} onChange={onChange} activeTab={queryParams.activeTab} setActiveTab={setActiveTab} scrollAreaRef={scrollAreaRef}/>);
                }}
                            </ProcessQueryStringController>);
        }}
                </ProcessController>
            </PersistenceSettingsContextProvider>
        </ProcessBlueprintContext.Provider>);
}
const defaultLookups: EditProcessTemplateLookupData = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
    processVariableSet: emptyVariableSetResource(""), // Empty spaceId while variable sets are space-scoped
    libraryVariableSets: [],
    templates: [],
    spaces: [],
};
interface ProcessStepsOrListLayoutProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRefResource: GitRefResource | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
function ProcessStepsOrListLayout({ lookupData, busy, doBusyTask, errors, gitRefResource, scrollAreaRef }: ProcessStepsOrListLayoutProps) {
    const processQueryStringContext = useProcessQueryStringContext();
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    const intent = intentFromFilter(processQueryStringContext.state.queryFilter);
    if (intent === ProcessPageIntent.Unknown) {
        return (<ProcessListLayout gitRef={gitRefResource?.CanonicalName} onProcessSave={() => Promise.resolve()} titleAccessory={undefined} releaseCreationPackageStepId={undefined} lookups={lookupData} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource}/>);
    }
    return (<ProcessStepsLayout gitRef={gitRefResource?.CanonicalName} titleAccessory={undefined} stepSlug={undefined} setShowK8sStatusItem={undefined} onProcessSave={() => Promise.resolve()} changeGitRef={noOp} // todo
     refreshModel={() => Promise.resolve(true)} projectStatus={undefined} includedLibraryVariableSetIds={[]} releaseCreationPackageStepId={undefined} lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource} isTenanted={true} onValidationError={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" })}/>);
}
function applyCommonLogicToProcessResource(process: IProcessResource, workerPoolsSummary: WorkerPoolsSummaryResource, getPluginForAction: (actionId: string) => ActionPlugin) {
    const availableWorkerPools = loadAvailableWorkerPools(workerPoolsSummary);
    process.Steps.forEach((step) => {
        step.Actions.forEach((action) => {
            const plugin = getPluginForAction(action.Id);
            const runOn = whereConfiguredToRun(!!step.Properties["Octopus.Action.TargetRoles"], action, availableWorkerPools, plugin, ProcessType.ProcessTemplate);
            if (runOn) {
                if (!isRunOnServerOrWorkerPool(runOn)) {
                    action.Container = { FeedId: null, Image: null, GitUrl: null, Dockerfile: null };
                }
                else {
                    if (runOn.executionLocation === ExecutionLocation.ProjectSelectedWorker) {
                        step.Properties["Octopus.Action.TargetRoles"] = "";
                    }
                    if (runOn.executionLocation !== ExecutionLocation.ProjectSelectedWorker && runOn.executionLocation !== ExecutionLocation.ProjectSelectedWorkerForRoles) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolId = null!;
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolVariable = null!;
                    }
                    action.Container = runOn.container;
                }
            }
            if (!action.Name || action.Name.length === 0) {
                const primaryPackage = GetPrimaryPackageReference(action.Packages);
                if (primaryPackage) {
                    action.Name = primaryPackage.PackageId;
                }
            }
        });
    });
}
function EditProcessTemplatePageInternal({ cleanModel, model, doBusyTask, busy, errors, lookupData, icons, iconMetadata, onChange, gitRefResource, gitRef, setGitRef, gitPersistenceSettings, activeTab, setActiveTab, scrollAreaRef, }: EditProcessTemplateInternalProps) {
    const process = useProcessContext();
    const processIdentifier = process.state.processIdentifier;
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const errorSelectors = useProcessErrorSelectors();
    const navigation = useSpaceAwareNavigation();
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const { openDialog: openCommitPublishShareDialog, isOpen: isCommitPublishShareDialogOpen, closeDialog: closeCommitPublishShareDialog } = useDialogTrigger();
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen } = useDialogTrigger();
    const [dismissedShareAndPublishCallout, setDismissed] = useLocalStorage(`Octopus.Callout.ProcessTemplate.ShareAndPublish.${cleanModel.Slug}.Dismissed`, false);
    const [wizardSteps, setWizardSteps] = useState<ProcessTemplateWizardSteps[]>([]);
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState<boolean>(false);
    const { result: publishedVersions, refetch: reloadPublishedVersions } = useQuery((repository) => repository.ProcessTemplates.getVersions(cleanModel.Slug), [cleanModel.Slug], "Get process template versions");
    const { result: sharedSpaces, refetch: reloadSharedSpaces } = useQuery((repository) => repository.ProcessTemplates.getSharedSpaces(model.Slug), [model.Slug], "Get spaces process template is shared to");
    //This is required to force a reload of the wizard dialog when sharing or publishing details are changed
    const sha = new jsSHA("SHA-1", "TEXT", { encoding: "UTF8" });
    sha.update(JSON.stringify(sharedSpaces) + JSON.stringify(publishedVersions?.[0] ?? ""));
    const reloadKey = sha.getHash("HEX");
    const isTemplateShared = sharedSpaces?.SharedToAllSpaces || (sharedSpaces && sharedSpaces.IndividuallySharedSpaceIds.length > 0);
    const { execute: handleStopShare } = useMutation({
        name: "Stop sharing process template",
        action: async (repository: Repository, sharedSpaces: SharedSpaces) => {
            const shareCommand: ShareTemplateToManySpacesBffCommand = {
                ProcessTemplateId: model.Id,
                ShareToAllSpaces: sharedSpaces.shareToAll,
                IndividuallySharedSpaceIds: sharedSpaces.individualSpacesSharedTo,
            };
            return await repository.ProcessTemplates.shareToSpace(shareCommand);
        },
        afterComplete: async (repository, output) => {
            reloadSharedSpaces();
            const snackbarMessage = "Process template has been unshared from all spaces.";
            showSnackbar(snackbarMessage);
        },
    });
    const { execute: handleQuickCommit } = useMutation({
        name: "Commit process template only",
        action: async (repository: Repository) => {
            await saveProcess(false);
        },
        afterComplete: async () => {
            errorActions.clearErrors();
            warningActions.clearWarnings();
            showSnackbar("Process template committed successfully.");
        },
        onError: (error) => {
            errorActions.setErrors(getErrorsFromError(error), process.selectors);
            warningActions.clearWarnings();
        },
    });
    const saveProcess = async (isNavigationConfirmation: boolean, commitMessage?: CommitMessageWithDetails) => {
        if (isNavigationConfirmation) {
            setWizardSteps([ProcessTemplateWizardSteps.Commit]);
            openCommitPublishShareDialog();
        }
        else {
            const { modelProcess: clientProcessResource } = process.selectors.getProcessesForMerge();
            applyCommonLogicToProcessResource(clientProcessResource, lookupData.workerPoolsSummary, process.selectors.getActionPlugin);
            const updatedProcessTemplate: ModifyProcessTemplateCommand = {
                ...model,
                Steps: clientProcessResource.Steps,
                ChangeDescription: getFormattedCommitMessage(commitMessage ?? { summary: "", details: "" }, getDefaultCommitMessage()),
            };
            await repository.ProcessTemplates.modify(updatedProcessTemplate);
            process.actions.refreshFromServer();
            if (!hasSteps(process.state.model)()) {
                if (!isProcessTemplateProcessIdentifier(processIdentifier)) {
                    throw new Error("This page only supports process templates");
                }
                const gitRef = model.GitRef;
                navigation.navigate(links.editProcessTemplatePage.generateUrl({ slug: processIdentifier.slug }, { gitRef }));
            }
        }
    };
    const reloadDataAfterSave = () => {
        reloadPublishedVersions();
        reloadSharedSpaces();
    };
    const publishButton: PageAction = {
        key: "PublishButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ActionButton label={"Publish"} type={ActionButtonType.Secondary} accessibleName={"Publish"} onClick={() => {
                setWizardSteps([ProcessTemplateWizardSteps.Publish]);
                openCommitPublishShareDialog();
            }}/>
            </PermissionCheck>),
    };
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    setWizardSteps([ProcessTemplateWizardSteps.Commit, ProcessTemplateWizardSteps.Publish]);
                }
                else {
                    setWizardSteps([ProcessTemplateWizardSteps.Commit, ProcessTemplateWizardSteps.Publish, ProcessTemplateWizardSteps.Share]);
                }
                openCommitPublishShareDialog();
            }} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy || !hasPermission(Permission.PlatformHubEdit)}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy || !hasPermission(Permission.PlatformHubEdit)} onClick={async () => {
                await handleQuickCommit();
            }}/>
                </div>
            </PermissionCheck>),
    };
    const onParametersChanged = useCallback((parameters: ActionTemplateParameterResource[]) => {
        const resource: ProcessTemplateResource = { ...model, Parameters: parameters.map((p) => (isProcessTemplateParameterResource(p) ? p : { ...p, Values: [] })) };
        onChange(resource);
    }, [onChange, model]);
    let secondaryActions: PageAction[] = [addStepButton, publishButton];
    if (activeTab === "process") {
        secondaryActions = [addStepButton, publishButton];
    }
    else if (activeTab === "settings") {
        secondaryActions = [publishButton];
    }
    else if (activeTab === "parameters") {
        const parametersActions = buildProcessTemplateParametersActions(model.Parameters || [], onParametersChanged);
        secondaryActions = [...parametersActions, publishButton];
    }
    const onSettingsChange = useCallback((settings: ProcessTemplateResource) => {
        const resource: ProcessTemplateResource = { ...model, ...settings };
        onChange(resource);
    }, [onChange, model]);
    const processFromContext = process.state.model.process ? process.selectors.getProcessResource() : null;
    if (processFromContext) {
        if (!isEqual(model.Steps, processFromContext.Steps)) {
            const resource: ProcessTemplateResource = { ...model, Steps: processFromContext.Steps };
            onChange(resource);
        }
    }
    function getDefaultCommitMessage(): string {
        return "Update process template";
    }
    const overflowActions: SimpleMenuItem[] = [];
    if (isAllowed({ permission: Permission.PlatformHubEdit })) {
        overflowActions.push({
            type: "button",
            label: "Share",
            onClick: () => {
                setWizardSteps([ProcessTemplateWizardSteps.Share]);
                openCommitPublishShareDialog();
            },
        });
        overflowActions.push({
            type: "divider",
        });
        overflowActions.push({
            type: isTemplateShared ? "button" : "disabled",
            label: "Stop sharing this template",
            onClick: openStopShareDialog,
        });
    }
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} gitPersistenceSettings={gitPersistenceSettings}/>;
    const shouldShowShareChip = sharedSpaces === null ? false : hasPermission(Permission.PlatformHubEdit) && cleanModel.Steps.length > 0 && !isTemplateShared;
    const shouldShowShareAndPublishCallout = sharedSpaces === null ? false : hasPermission(Permission.PlatformHubEdit) && !dismissedShareAndPublishCallout && cleanModel.Steps.length > 0 && publishedVersions?.length === 0 && !isTemplateShared;
    const anyProcessSpecificErrors = errorSelectors.getActionsWithErrors().length > 0 || errorSelectors.getStepsWithErrors().length > 0;
    // The only known settings-specific error is if no name is provided.
    const anySettingsSpecificErrors = errors.some((e) => (OctopusError.isOctopusError(e) && e.Details ? Object.keys(e.Details).some((k) => k === "Name") : false));
    const tabWarningMessage = "Errors found in this section";
    function GetCallout(): PageContentCalloutType | undefined {
        if (showInitialSuccessCallout) {
            return {
                type: "custom",
                content: (<Callout type={"success"} title={`${model.Name} was successfully committed, published and shared.`} canClose={true} onClose={() => setShowInitialSuccessCallout(false)}>
                        This template can now be used in deployment processes.
                    </Callout>),
            };
        }
        if (shouldShowShareAndPublishCallout) {
            return {
                type: "custom",
                content: (<ShareAndPublishCallout onPublishShareClick={() => {
                        setWizardSteps([ProcessTemplateWizardSteps.Publish, ProcessTemplateWizardSteps.Share]);
                        openCommitPublishShareDialog();
                    }} onDismiss={() => setDismissed(true)} templateName={model.Name}/>),
            };
        }
    }
    return (<>
            <header className={styles.pageHeaderContainer}>
                <div className={styles.breadcrumbContainer}>{<Breadcrumbs items={[{ label: "Process Templates", pageUrl: links.processTemplatesPage.generateUrl() }]} showBackIcon={true}/>}</div>
                <div className={styles.titleContainer}>
                    <Avatar src={getProcessTemplateIconUrl(model)} shape={"squared"} size={32} alt={"Process template icon"}/>
                    <h1 title={model.Name ?? "Edit process template"} className={styles.title}>
                        {model.Name ?? "Edit process template"}
                    </h1>
                    {shouldShowShareChip ? <Chip backgroundColor={themeTokens.color.badge.background.neutral}>Not shared</Chip> : undefined}
                </div>
            </header>

            <LegacyForm devToolsDirtyTrackingKey="Process Template" confirmNavigateSaveLabel={"Commit changes..."} disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={saveProcess} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={busy}>
                {({ FormContent }) => (<PageContent busy={busy} header={{
                title: "Process Editor",
                primaryAction: commitButton,
                pageActions: secondaryActions,
                contextSelector: titleAccessory,
                overflowActions: overflowActions,
            }} callout={GetCallout()} currentTab={activeTab} onTabChanged={setActiveTab} tabs={[
                {
                    label: "Process",
                    value: "process",
                    warning: anyProcessSpecificErrors ? tabWarningMessage : undefined,
                    content: <ProcessStepsOrListLayout lookupData={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} gitRefResource={gitRefResource} scrollAreaRef={scrollAreaRef}/>,
                },
                {
                    label: "Parameters",
                    value: "parameters",
                    content: <ProcessTemplateParametersTab parameters={model.Parameters} onChange={onParametersChanged}/>,
                },
                {
                    label: "Settings",
                    value: "settings",
                    warning: anySettingsSpecificErrors ? tabWarningMessage : undefined,
                    content: (<FormContent hideExpandAll={false}>
                                        <EditProcessTemplateSettingsTab processTemplate={model} onChange={onSettingsChange} iconSvgResources={icons} iconMetadata={iconMetadata}/>
                                    </FormContent>),
                },
                {
                    label: "Versions",
                    value: "versions",
                    content: <ProcessTemplateVersions publishedVersions={publishedVersions}/>,
                },
            ]}/>)}
            </LegacyForm>

            {publishedVersions ? (<ProcessTemplateWizardDialog open={isCommitPublishShareDialogOpen} busy={busy} errors={errors} model={model} gitRef={gitRef} reloadDataAfterSave={reloadDataAfterSave} lookupData={lookupData} sharedSpaces={sharedSpaces} publishedVersions={publishedVersions} stepsToShow={wizardSteps} getDefaultCommitMessage={getDefaultCommitMessage} commit={saveProcess} setShowInitialSuccessCallout={setShowInitialSuccessCallout} closeDialog={closeCommitPublishShareDialog} key={reloadKey}/>) : null}

            <Dialog open={isStopShareDialogOpen}>{<StopShareTemplateDialog onSave={handleStopShare} onClose={openStopShareDialog}/>}</Dialog>
        </>);
}
interface ShareAndPublishCalloutProps {
    templateName: string;
    onPublishShareClick: () => void;
    onDismiss: () => void;
}
function ShareAndPublishCallout({ templateName, onPublishShareClick, onDismiss }: ShareAndPublishCalloutProps) {
    return (<Callout type={"success"} title={`${templateName} was successfully committed.`} canClose={true} onClose={onDismiss}>
            To use this template in spaces, you'll need to{" "}
            <a href="#" onClick={(e) => {
            e.preventDefault();
            onPublishShareClick();
        }}>
                <strong>publish and share</strong>
            </a>{" "}
            this template.
        </Callout>);
}
function ProcessTemplateVersions({ publishedVersions }: {
    publishedVersions: ProcessTemplateVersionResource[] | null;
}) {
    if (publishedVersions === null) {
        return null;
    }
    if (publishedVersions.length === 0) {
        return (<OnboardingPage title="No versions found for this template" intro="Create versions of process templates on any branch to test and publish to the rest of your team. To get started on publishing a version, select “Publish” in the top right corner." learnMore={null}/>);
    }
    return (<div>
            <SimpleDataTable columns={[
            {
                title: "Version",
                render: (data) => (<div className={styles.versionContainer}>
                                <b>{data.Version}</b>
                                {data.IsPreRelease ? <Chip backgroundColor={themeTokens.color.chip.filled.background.info}>Pre-release</Chip> : null}
                            </div>),
                columnSize: "medium",
            },
            { title: "Git reference", render: (data) => <GitRefChip vcsRef={{ GitRef: data.GitRef, GitCommit: data.GitCommit }} showCommit={true}/>, columnSize: "medium" },
            { title: "Published", render: (data) => DateFormatter.dateToLongFormat(data.PublishedDate) },
        ]} data={publishedVersions} getRowKey={(data) => data.Version}/>
        </div>);
}
const styles = {
    versionContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
    stopSharingMenuItem: css({
        color: themeTokens.color.text.danger,
    }),
    pageHeaderContainer: css({
        display: "flex",
        flexDirection: "column",
        paddingBottom: space[32],
        gap: "unset",
    }),
    breadcrumbContainer: css({
        display: "flex",
        alignItems: "flex-start",
        height: "1.75rem",
    }),
    titleContainer: css({
        flex: "1",
        display: "flex",
        gap: space[12],
        alignItems: "center",
        overflow: "hidden",
    }),
    title: css({
        all: "unset",
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
const addStepButton: PageAction = {
    type: "custom",
    content: <ContextAddStepButton />,
    key: "Add Step",
};
export async function editProcessTemplateLoader(repository: Repository, gitRef: string, slug: string): Promise<LoaderData> {
    const platformHubConnectionConfiguration = await repository.PlatformHubConnection.get();
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(platformHubConnectionConfiguration.PersistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    const processTemplate = await repository.ProcessTemplates.get(slug, gitRef);
    return {
        processTemplate,
        platformHubConnectionConfiguration,
        initialValidationResult,
    };
}
interface LoaderData {
    processTemplate: ProcessTemplateResource;
    platformHubConnectionConfiguration: PlatformHubConnectionConfigurationResource;
    initialValidationResult: ValidateGitRefV2Response;
}
export default function EditProcessTemplatePage(props: EditProcessTemplateProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress, errors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    return <EditWithAllContext loaderData={props.loaderData} slug={props.slug} doBusyTask={doBusyTask} busy={isInProgress} errors={errors} queryParams={props.queryParams} setQueryParams={props.setQueryParams} scrollAreaRef={props.scrollAreaRef}/>;
}
